import React, { useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { TextInput, Button, Text, Surface, Avatar } from 'react-native-paper';
import { supabase } from '../../services/supabase';
import { AppLayout } from '../../components/AppLayout';
import { appStateService } from '../../services/appState';

export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // 1. Autenticar o usuário
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) throw signInError;
      
      // 2. Buscar dados do usuário
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');
      
      // 3. Buscar dados da empresa e código do usuário
      const { data: userData, error: userError } = await supabase
        .from('usuarios')
        .select('empresa, codusur')
        .eq('id', user.id)
        .single();
        
      if (userError) throw userError;
      if (!userData?.empresa) throw new Error('Empresa não encontrada');
      
      // 4. Buscar a rota da API para a empresa
      const { data: empresaData, error: empresaError } = await supabase
        .from('empresa')
        .select('rota')
        .eq('id', userData.empresa)
        .single();
        
      if (empresaError) throw empresaError;
      if (!empresaData?.rota) throw new Error('Rota da API não encontrada para a empresa');
      
      // 5. Armazenar os dados no estado global
      appStateService.updateUserData({
        id: user.id,
        email: user.email,
        empresa: userData.empresa,
        codusur: userData.codusur,
        rotaApi: empresaData.rota
      });
      
      console.log('Login realizado com sucesso. Rota da API:', empresaData.rota);
    } catch (err: any) {
      console.error('Erro no login:', err);
      setError(err.message === 'Invalid login credentials'
        ? 'Email ou senha inválidos'
        : 'Erro ao fazer login. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <View style={styles.container}>
        <View style={styles.header}>
          <Avatar.Icon 
            size={64} 
            icon="chart-line-variant" 
            style={styles.logo}
          />
          <Text variant="headlineMedium" style={styles.title}>SmartScore</Text>
          <Text variant="titleMedium" style={styles.subtitle}>Sistema de Cobrança</Text>
        </View>

        <form onSubmit={(e) => {
          e.preventDefault();
          handleLogin();
        }}>
          <View style={styles.form}>
            <TextInput
              label="Email"
              value={email}
              onChangeText={setEmail}
              mode="outlined"
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              left={<TextInput.Icon icon="email" />}
              style={styles.input}
              error={!!error}
            />

            <TextInput
              label="Senha"
              value={password}
              onChangeText={setPassword}
              mode="outlined"
              secureTextEntry={!showPassword}
              autoComplete="current-password"
              left={<TextInput.Icon icon="lock" />}
              right={
                <TextInput.Icon 
                  icon={showPassword ? "eye-off" : "eye"} 
                  onPress={() => setShowPassword(!showPassword)}
                />
              }
              style={styles.input}
              error={!!error}
            />

            {error && (
              <Text style={styles.error}>{error}</Text>
            )}

            <Button
              mode="contained"
              onPress={handleLogin}
              loading={loading}
              style={styles.button}
              contentStyle={styles.buttonContent}
            >
              Entrar
            </Button>

            <Button
              mode="text"
              onPress={() => {}}
              style={styles.forgotPassword}
            >
              Esqueceu sua senha?
            </Button>
          </View>
        </form>
      </View>
    </AppLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    backgroundColor: '#1a73e8',
    marginBottom: 16,
  },
  title: {
    color: '#1a73e8',
    fontWeight: 'bold',
  },
  subtitle: {
    color: '#5f6368',
    marginTop: 4,
  },
  form: {
    gap: 16,
  },
  input: {
    backgroundColor: 'white',
  },
  error: {
    color: '#dc3545',
    textAlign: 'center',
  },
  button: {
    marginTop: 8,
  },
  buttonContent: {
    height: 48,
  },
  forgotPassword: {
    marginTop: 8,
  },
});
